import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { PracticeCourse } from '../../../types/practiceCourses';

type UseSubjectSelectionDialogParams = {
  courses: PracticeCourse[];
  isOpen: boolean;
  onConfirm: (course: PracticeCourse) => void;
};

type UseSubjectSelectionDialogResult = {
  handleConfirm: () => void;
  handleSelectCourse: (courseId: string) => void;
  hasCourses: boolean;
  isConfirmDisabled: boolean;
  selectedCourseId: string | null;
  t: (key: string) => string;
};

/**
 * Single-selection logic for the subject (Popup 1) dialog (TANAI-1869).
 */
function useSubjectSelectionDialog({ courses, isOpen, onConfirm }: UseSubjectSelectionDialogParams): UseSubjectSelectionDialogResult {
  const { t } = useTranslation(['chatbot', 'actions']);
  const [selectedCourseId, setSelectedCourseId] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setSelectedCourseId(null);
    }
  }, [isOpen]);

  const handleSelectCourse = useCallback((courseId: string) => {
    setSelectedCourseId(courseId);
  }, []);

  const handleConfirm = useCallback(() => {
    const course = courses.find((item) => item.id === selectedCourseId);
    if (course) {
      onConfirm(course);
    }
  }, [courses, onConfirm, selectedCourseId]);

  const hasCourses = courses.length > 0;
  const isConfirmDisabled = !selectedCourseId;

  return {
    handleConfirm,
    handleSelectCourse,
    hasCourses,
    isConfirmDisabled,
    selectedCourseId,
    t,
  };
}

export default useSubjectSelectionDialog;
